import { Component, Vue } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import { CookieStorageService } from '@questelservices/common';
import { Roles } from '@/common/Roles';
import ConfirmDialogModule from '@/modules/ConfirmDialogModule';
import AuthorizationModule from '@/modules/AuthorizationModule';
import CommonModule from '@questelservices/common';
import UserModule from '@/modules/UserModule';
import VueToast from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';
import { QuestelServicesConstants } from '@/models/QuestelServicesConstants';
import moment from 'moment';
import CommonLoader from '@/components/Common/CommonLoader.vue';


@Component({ components: { CommonLoader } })
export default class VueBase extends Vue {

  protected roles = Roles;
  protected cookieService: CookieStorageService = new CookieStorageService();
  protected confirmDialogStore: ConfirmDialogModule = getModule(ConfirmDialogModule, this.$store);
  protected authorizationModule: AuthorizationModule = getModule(AuthorizationModule, this.$store);
  protected userModule: UserModule = getModule(UserModule, this.$store);
  protected commonModule: CommonModule = getModule(CommonModule, this.$store);

  constructor() {
    super();
    Vue.use(VueToast);
  }

  /**
   * Shows a confirm dialog with the specified text message, title, and other options.
   *
   * @param {string} message The message to display.
   * @param {Function} onConfirmFnc The callback function to call once the user clicks YES or NO.
   * @param {string} title [OPTIONAL] The title of the dialog (default = 'Confirm').
   * @param {number} width [OPTIONAL] The width of the dialog (default = 500).
   * @param {string} okBtnText [OPTIONAL] The text to display on the YES button (default = 'Yes').
   * @param {string} cancelBtnText [OPTIONAL] The text to display on the NO button (default = 'No').
   * @param {boolean} isNavAwayConfirm [OPTIONAL] The flag which tell what type of confirm message to show (default = 'false').
   */
  protected showConfirmDialog(message: string, onConfirmFnc: Function, title: string = 'Confirm', width: number = 500, okBtnText: string = 'Yes', cancelBtnText: string = 'No'): void {
    this.confirmDialogStore.toggle({ message: message, width: width, onConfirmFunc: onConfirmFnc, title: title, show: true, okButtonText: okBtnText, cancelButtonText: cancelBtnText});
  }

  /**
   * Shows an alert dialog with the specified message, title, and other options.
   *
   * @param {string} message The message to display.
   * @param {string} title [OPTIONAL] The title of the dialog (default = no title is displayed).
   * @param {string} options [OPTIONAL] The options to use to display the dialog.
   */
  protected showAlertDialog(message: string, title?: string, options?: any): void {
    Vue.prototype.$alert(message, title, options);  // This is defined in the 'mounted' event of the APP.VUE file
  }

  /**
   * Displays a toast message (by default at the bottom of the screen) which stays visible for a certain number of seconds (by default 2.5 seconds), then disappears. The user can click it to dismiss it immediately.
   *
   * @see https://www.npmjs.com/package/vue-toast-notification
   *
   * @param {string} message The message to display.
   * @param {string} type [OPTIONAL] The type of toast message to display (default = 'success').
   * @param {number} duration [OPTIOANL] The number of milliseconds to leave the toast message on the screen before automatically hiding it (default = 5000).
   * @param {string} position [OPTIONAL] The location of the toast message on the screen (default = 'top')
   * @param {boolean} queue [OPTIONAL] If TRUE, showing a 2nd toast message will wait for the 1st to disappear before showing. If FALSE, both toast messages would show at the same time. (default = false)
   */
  protected showToastMessage(
    message: string,
    type: 'default'|'info'|'success'|'warning'|'error' = 'success',
    duration: number = 5000,
    position: 'top'|'bottom'|'top-right'|'bottom-right'|'top-left'|'bottom-left' = 'top',
    queue: boolean = false
  ): void {
    Vue.$toast.open({ message: message, type: type, position: position, duration: duration, dismissible: true, queue: queue });
  }

  /**
   * By default, displays a gray translucent overlay on top of the current page, with a spinning "Loading" circle in the middle of the page.  However, you can override the default functionality by passing in
   * custom values to the "properties" parameter and/or "slots" parameter. See the following URL for more details. To use this function, call it and save the return value into a variable... then call
   * the "hide()" method on that variable once you're ready to hide/remove the overlay.
   *
   * @see https://www.npmjs.com/package/vue-loading-overlay
   *
   * @param {any} properties [OPTIONAL] An object that holds the properties to pass into the "vue-loading-overlay" component to customize it's look and/or behavior.
   * @param {any} slots [OPTIONAL] An object that holds the slots (references to DOM elements) to use to customize the "Loading" circle in the middle of the page. Possible values include: default, before, after
   */
  protected showLoader(properties: any = {}, slots: any = { default: this.$createElement('common-loader')}): any {
    return this.$loading.show(properties, slots);
  }

  protected currentUserHasAtLeastOneRole(roleNames: string[]): boolean {
    if (typeof roleNames === 'undefined' || roleNames === null || !Array.isArray(roleNames) || !this.commonModule.isAuthenticated) {
      return false;
    }
    return this.authorizationModule.userRoles.some((role: string) => roleNames.includes(role));
  }

  protected validatePageAccess(rolesWithAccess: string[]): void {
    if (!rolesWithAccess || rolesWithAccess.length == 0 || !this.currentUserHasAtLeastOneRole(rolesWithAccess)) {
      const redirectionPath = this.$route.fullPath;
      this.authorizationModule.setPromptCancelImpersonation({promptCancel: true, redirectionPath: redirectionPath});
    }
  }

  protected redirectToForbiddenPage(): void {
    this.$router.push({ name: 'forbidden', query: { redirect: this.$route.fullPath } });
  }

  protected get isAdLogin(): boolean {
    return this.commonModule.authenticatedUser == null || this.commonModule.authenticatedUser.isAdLogin === true;
  }

  protected get currentClientGlobalId(): string {
    const userInfo = this.userModule.getCurrentUser;
    return userInfo && userInfo.ClientGlobalId
      ? userInfo.ClientGlobalId.toString()
      : '';
  }

  /**
   * Uses the Moment library to take the specified Date object and format it to a string. If the specified date is UNDEFINED, NULL, or a BLANK STRING, then NULL is returned. Otherwise, a string is returned.
   *
   * @param {Date} date The Date object to format.
   * @param {string} format [OPTIONAL] The format to use to format the Date object. If not specified, the format is "DD-MMM-YYYY".
   */
  protected formatDate(date: Date | string | null | undefined, format?: string): string | null {
    if (typeof(date) === 'undefined' || date === null || date === '') {
      return null;
    }
    return this.$moment(date).utc().format(format ?? QuestelServicesConstants.defaultDateFormat);
  }

  protected getDateWithLocalTimeZome(currentDate: Date | string | null) : Date | null {
    if(!!currentDate) {
      let momentDate = moment(new Date(currentDate));
      return new Date(momentDate.utc().year(), momentDate.utc().month(), momentDate.utc().date());
    }
    return null;
  }
}
